import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../../library/button/button';
import Select from '../../../../library/select/select';
import Text from '../../../../library/text/text';
import Tooltip from '../../../../library/tooltip/tooltip';
import BackButton from '../../../../components/back-button/back-button';
import FitmentSelect from './components/fitment-select/fitment-select';
import FitmentTireSize from './components/fitment-tire-size/fitment-tire-size';
import { ShieldCheckSolidIcon } from '../../../../shared/icons/icons';
import Typography from '../../../typography/typography';
import VehicleDescription from '../../../vehicle-description/vehicle-description';
import { CRUMBS, EVENTS, PROP_TYPES } from '../../fitment-constants';
import FitmentCrumbs from '../fitment-crumbs/fitment-crumbs';
import FitmentRecentVehicles from '../fitment-recent-vehicles/fitment-recent-vehicles';
import {
  assemblyPropType,
  modelPropType,
  rideHeightPropType,
  trimPropType
} from './fitment-by-vehicle-util';

import { useForm, usePrevious } from '../../../../shared/hooks/hooks';
import { fieldTypes } from '../../../../shared/hooks/use-form/use-form';

import {
  CANADA_ISO_CODE,
  COUNTRIES,
  PROVINCES,
  STATES
} from '../../../../shared/constants/form';

import './fitment-by-vehicle.scss';

function FitmentByVehicle(props) {
  const {
    assemblies,
    textOverrides,
    hideBackButton,
    hideVehicleCrumbs = false,
    hideDelete,
    hideLicensePlate = false,
    isEditing,
    isLoggedIn,
    isShowingRecentFitments,
    isTreadwellStart,
    isCustomDriverTypeShowing,
    isYearAfterModel,
    canToggleVehicle,
    getOptionalSizes,
    makes,
    models,
    onBackToMyVehicles,
    onCrumbSelected,
    onEvent,
    onMakeSelected,
    onModelSelected,
    onFitmentsToggled,
    onFitmentSelected,
    onFocusDropdown,
    onLicensePlateCancel,
    onLicensePlateVehicleConfirmed,
    onRideHeightSelected,
    onShopProducts,
    onSelectFitment,
    onTrimSelected,
    onYearSelected,
    recentFitments,
    renderDriverTypesInput,
    renderFitmentBanner,
    renderSavedFitmentsMessage,
    renderSelectedFitment,
    renderShopProductsButton,
    renderZipCodeInput,
    rideHeights,
    savedFitments,
    selectedAssembly,
    selectedCrumb,
    selectedDriverType,
    selectedFitment,
    selectedMake,
    selectedModel,
    selectedRideHeight,
    selectedTrim,
    selectedYear,
    setSelectedAssembly,
    setIsShowingRecentFitments,
    shouldAutofocusLicensePlate,
    shouldCallShopProducts,
    showShopProductsButton,
    temporaryFitment,
    trims,
    years,
    onSubmitLicensePlate,
    defaultVehicleLicensePlateAddress,
    onFocusLicensePlateDropdown,
    zipCodeValue
  } = props;

  const fieldConfig = [
    {
      ...fieldTypes.text,
      name: 'licensePlate',
      value: ''
    },
    {
      ...fieldTypes.country,
      value: defaultVehicleLicensePlateAddress?.country
    },
    {
      ...fieldTypes.state,
      value: defaultVehicleLicensePlateAddress?.state
    }
  ];
  const {
    fields: { country, state, licensePlate },
    updateField
  } = useForm(fieldConfig, {
    validateWithTimeout: true
  });

  const vehicleLicensePlateRef = useRef();

  const previousSavedFitments = usePrevious(savedFitments);
  const previousSelectedFitment = usePrevious(selectedFitment);
  const wasFitmentUnselected = previousSelectedFitment && !selectedFitment;

  const yearOptions = useMemo(
    () =>
      years?.map(value => ({
        label: value.description,
        value: value.id
      })),
    [years]
  );
  const selectedYearOption = yearOptions?.find(
    yearOption => yearOption?.value === selectedYear?.id
  );
  const makeOptions = useMemo(
    () => makes?.map(value => ({ label: value, value })),
    [makes]
  );
  const selectedMakeOption = makeOptions?.find(
    makeOption => makeOption.value.toLowerCase() === selectedMake?.toLowerCase()
  );
  const modelOptions = useMemo(
    () => models?.map(model => ({ label: model.description, value: model.id })),
    [models]
  );
  const selectedModelOption = modelOptions?.find(
    modelOption => modelOption.value === selectedModel?.id
  );
  const trimOptions = useMemo(
    () => trims?.map(trim => ({ label: trim.description, value: trim.id })),
    [trims]
  );
  const selectedTrimOption = trimOptions?.find(
    trimOption => trimOption.value === selectedTrim?.id
  );
  const rideHeightOptions = useMemo(
    () =>
      rideHeights?.map(rideHeight => ({
        label: rideHeight.description,
        value: rideHeight.id
      })),
    [rideHeights]
  );
  const selectedRideHeightOption = rideHeightOptions?.find(
    rideHeightOption => rideHeightOption.value === selectedRideHeight?.id
  );

  const handleBackToMyVehicles = useCallback(() => {
    onFitmentsToggled?.(true);
    if (onBackToMyVehicles) {
      onBackToMyVehicles();
    }
  }, [onBackToMyVehicles, onFitmentsToggled]);

  const hasRecentFitments =
    Array.isArray(recentFitments) && recentFitments.length > 0;

  const hasSavedFitments =
    Array.isArray(savedFitments) && savedFitments.length > 0;
  const isOnlyShowingRecentFitments =
    isShowingRecentFitments && (hasRecentFitments || hasSavedFitments);
  const isShowingFitmentCrumbs =
    (Boolean(selectedCrumb) || isEditing) && !hideVehicleCrumbs;

  const yearCrumb = {
    isActive: isYearAfterModel
      ? Boolean(selectedMake) && Boolean(selectedModel)
      : Boolean(selectedYear),
    isClickable: isYearAfterModel ? Boolean(selectedModel) : true,
    isCompleted: Boolean(selectedYear),
    label: CRUMBS.YEAR
  };

  const makeCrumb = {
    isActive: isYearAfterModel ? Boolean(selectedMake) : Boolean(selectedYear),
    isClickable: isYearAfterModel ? true : yearCrumb.isCompleted,
    isCompleted: Boolean(selectedMake),
    label: CRUMBS.MAKE
  };

  const modelCrumb = {
    isActive: isYearAfterModel
      ? Boolean(selectedMake)
      : Boolean(selectedYear) && Boolean(selectedMake),
    isClickable: makeCrumb.isCompleted,
    isCompleted: Boolean(selectedModel),
    label: CRUMBS.MODEL
  };

  const trimCrumb = {
    isActive:
      Boolean(selectedYear) && Boolean(selectedMake) && Boolean(selectedModel),
    isClickable: isYearAfterModel
      ? Boolean(selectedYear)
      : Boolean(selectedModel),
    isCompleted: Boolean(selectedTrim),
    label: CRUMBS.TRIM
  };

  const sizeCrumb = {
    isActive:
      Boolean(selectedYear) &&
      Boolean(selectedMake) &&
      Boolean(selectedModel) &&
      Boolean(selectedTrim) &&
      Boolean(selectedRideHeight),
    isClickable: trimCrumb.isCompleted,
    isCompleted: Boolean(selectedAssembly),
    label: CRUMBS.TIRE_WHEEL_SIZE
  };

  let crumbs = isYearAfterModel
    ? [makeCrumb, modelCrumb, yearCrumb, trimCrumb, sizeCrumb]
    : [yearCrumb, makeCrumb, modelCrumb, trimCrumb, sizeCrumb];

  if (isTreadwellStart) {
    const isDrivingActive =
      sizeCrumb.isActive && Boolean(zipCodeValue) && Boolean(selectedAssembly);
    const isZipClickable =
      selectedCrumb === CRUMBS.DRIVING_LOCATION ||
      selectedCrumb === CRUMBS.DRIVING_TYPE;
    const zipCrumb = {
      isActive: isDrivingActive,
      isClickable: isZipClickable,
      isCompleted: isZipClickable && Boolean(zipCodeValue),
      label: CRUMBS.DRIVING_LOCATION
    };
    crumbs.push(zipCrumb);
    crumbs.push({
      isActive:
        isDrivingActive &&
        Boolean(selectedDriverType || isCustomDriverTypeShowing),
      isClickable: selectedCrumb === CRUMBS.DRIVING_TYPE,
      isCompleted:
        selectedCrumb === CRUMBS.DRIVING_TYPE && Boolean(selectedDriverType),
      label: CRUMBS.DRIVING_TYPE
    });
  }

  useEffect(() => {
    if (
      wasFitmentUnselected &&
      (selectedCrumb === '' ||
        selectedCrumb === CRUMBS.DRIVING_LOCATION ||
        selectedCrumb === CRUMBS.DRIVING_TYPE) &&
      typeof renderSelectedFitment === 'function'
    ) {
      handleBackToMyVehicles();
    }
  }, [
    handleBackToMyVehicles,
    renderSelectedFitment,
    selectedCrumb,
    wasFitmentUnselected
  ]);

  useEffect(() => {
    if (shouldAutofocusLicensePlate && vehicleLicensePlateRef.current) {
      vehicleLicensePlateRef.current.focus();
    }
  }, [shouldAutofocusLicensePlate, vehicleLicensePlateRef]);

  useEffect(() => {
    if (previousSavedFitments?.length < savedFitments?.length) {
      setIsShowingRecentFitments(true);
    }
  }, [savedFitments, previousSavedFitments, setIsShowingRecentFitments]);

  useEffect(() => {
    onEvent?.({
      name: EVENTS.VEHICLE_FITMENT_CRUMBS_SHOWING,
      payload: isShowingFitmentCrumbs && !isOnlyShowingRecentFitments
    });
  }, [isOnlyShowingRecentFitments, isShowingFitmentCrumbs, onEvent]);

  useEffect(() => {
    if (
      selectedCrumb !== CRUMBS.DRIVING_LOCATION &&
      selectedCrumb !== CRUMBS.DRIVING_TYPE &&
      !selectedRideHeight &&
      Array.isArray(rideHeights) &&
      rideHeights.length === 1
    ) {
      onRideHeightSelected(rideHeights[0]);
    }
  }, [selectedCrumb, selectedRideHeight, onRideHeightSelected, rideHeights]);

  function setCountry(value) {
    updateField(state.name, '');
    updateField(country.name, value);
  }

  function resetLicensePlateFormFields() {
    updateField(licensePlate.name, '');
    updateField(state.name, defaultVehicleLicensePlateAddress.state);
    updateField(country.name, defaultVehicleLicensePlateAddress.country);
  }

  if (isOnlyShowingRecentFitments) {
    return (
      <FitmentRecentVehicles
        addVehicleOnClick={() => {
          void onFitmentsToggled(false);
          if (licensePlate.value) {
            resetLicensePlateFormFields();
          }
        }}
        canToggleVehicle={canToggleVehicle}
        hideDelete={hideDelete}
        isLoggedIn={isLoggedIn}
        onEvent={onEvent}
        onFitmentSelected={onFitmentSelected}
        onSelectFitment={onSelectFitment}
        recentFitments={recentFitments}
        renderSavedFitmentsMessage={renderSavedFitmentsMessage}
        savedFitments={savedFitments}
        selectedFitment={selectedFitment}
      />
    );
  }

  return (
    <>
      {(!hideBackButton || isShowingFitmentCrumbs) && (
        <div className="fitment-by-vehicle__controls" styleName="controls">
          {(hasRecentFitments || hasSavedFitments) && !hideBackButton && (
            <BackButton
              className="fitment-by-vehicle-container__back-button"
              onClick={handleBackToMyVehicles}
              styleName="back-button"
              text="back to my vehicles"
            />
          )}
          {isShowingFitmentCrumbs && (
            <FitmentCrumbs
              crumbs={crumbs}
              onCrumbSelected={crumb => {
                onCrumbSelected(crumb);
                if (crumb === CRUMBS.YEAR && licensePlate.value) {
                  resetLicensePlateFormFields();
                }
              }}
              selectedCrumb={selectedCrumb}
              styleName={isTreadwellStart ? 'treadwell-crumbs' : ''}
            />
          )}
        </div>
      )}

      {isShowingFitmentCrumbs &&
        selectedCrumb !== CRUMBS.DRIVING_LOCATION &&
        selectedCrumb !== CRUMBS.DRIVING_TYPE &&
        (selectedYear?.description || selectedMake) && (
          <div styleName="vehicle-description">
            <VehicleDescription
              make={selectedMake}
              model={selectedModel?.description}
              rideHeight={selectedRideHeight?.description}
              trim={selectedTrim?.description}
              year={selectedYear?.description}
            />
          </div>
        )}
      {(selectedCrumb === CRUMBS.DRIVING_LOCATION ||
        selectedCrumb === CRUMBS.DRIVING_TYPE) &&
      renderFitmentBanner
        ? renderFitmentBanner?.({ fitment: selectedFitment })
        : null}
      {selectedCrumb === CRUMBS.YEAR && (
        <>
          <FitmentSelect
            onChange={yearOption =>
              onYearSelected(
                years?.find(year => year?.id === yearOption?.value)
              )
            }
            onFocus={onFocusDropdown}
            options={yearOptions}
            placeholder="Year"
            selectedOption={selectedYearOption}
          />
          {!hideLicensePlate && (
            <div styleName="license-plate-container">
              <Typography styleName="or" variant="H6" weight="black">
                OR
              </Typography>
              <Typography styleName="info-text" variant="H6" weight="black">
                Skip steps by entering your Vehicle license plate{' '}
                <Tooltip
                  content="Custom vanity plates or newer vehicles may not be found. If you're having trouble with the license plate lookup, please enter your vehicle's year, make and model to continue."
                  onClick={() =>
                    onEvent?.({
                      name: EVENTS.VEHICLE_FITMENT_LP_NEED_HELP
                    })
                  }
                  styleName="tooltip"
                />
              </Typography>
              <Typography styleName="disclaimer">
                <div>
                  <ShieldCheckSolidIcon styleName="icon" />
                </div>
                This data is protected and will only be used to detect your
                vehicle model
              </Typography>
              <Text
                label="Your vehicle license plate"
                name={licensePlate.name}
                onBlur={e => updateField(licensePlate.name, e.target.value)}
                onChange={e => updateField(licensePlate.name, e.target.value)}
                onFocus={onFocusLicensePlateDropdown}
                ref={vehicleLicensePlateRef}
                styleName={
                  !licensePlate?.value ? 'license-plate-select' : 'select'
                }
                value={licensePlate?.value?.toUpperCase()}
              />
              {licensePlate?.value && (
                <>
                  <Select
                    hideResetButton
                    isRequired
                    onChange={value => setCountry(value)}
                    options={COUNTRIES}
                    placeholder="Country"
                    styleName="select"
                    value={country.value}
                  />
                  <Select
                    error={state.error}
                    hideResetButton
                    isDisabled={!country.value}
                    isRequired
                    onChange={value => updateField(state.name, value)}
                    options={
                      country?.value?.value === CANADA_ISO_CODE
                        ? PROVINCES
                        : STATES
                    }
                    placeholder="State"
                    styleName="select"
                    value={state.value}
                  />
                </>
              )}
              {licensePlate.value && (
                <Button
                  disabled={
                    !(licensePlate.value && state.value && country.value)
                  }
                  isPrimary
                  onClick={() =>
                    onSubmitLicensePlate(
                      {
                        country: country?.value?.value,
                        licensePlate: licensePlate?.value,
                        state: state?.value?.value?.slice(3)
                      },
                      onMakeSelected,
                      onModelSelected,
                      onYearSelected,
                      onLicensePlateVehicleConfirmed,
                      onLicensePlateCancel
                    )
                  }
                  styleName="submit-btn"
                >
                  Submit
                </Button>
              )}
            </div>
          )}
        </>
      )}

      {selectedCrumb === CRUMBS.MAKE && (
        <FitmentSelect
          onChange={makeOption => onMakeSelected(makeOption?.value)}
          onFocus={onFocusDropdown}
          options={makeOptions}
          placeholder="Make"
          selectedOption={selectedMakeOption}
        />
      )}

      {selectedCrumb === CRUMBS.MODEL && (
        <FitmentSelect
          onChange={modelOption =>
            onModelSelected(
              models?.find(model => model?.id === modelOption?.value)
            )
          }
          onFocus={onFocusDropdown}
          options={modelOptions}
          placeholder="Model"
          selectedOption={selectedModelOption}
        />
      )}

      {selectedCrumb === CRUMBS.TRIM && (
        <div
          className="fitment-by-vehicle__trim-selection-wrapper"
          styleName="trim-selection-wrapper"
        >
          <FitmentSelect
            moreInfoContent={() => (
              <Typography styleName="trim-description" tag="div">
                Vehicle trim is an options package often found as a sticker or
                lettering on the trunk or tailgate of your vehicle. Some
                examples you might be familiar with include: DX, EX, ECO, FX,
                GT, Hybrid, LX, LTD, Pro, S, ST, STD, SVT, Sport as well as
                others.
                <Typography>
                  You can also find the trim using the vehicle identification
                  number (VIN). The VIN sticker is often on the driver’s side
                  door jamb.
                </Typography>
              </Typography>
            )}
            onChange={trimOption =>
              onTrimSelected(
                trims?.find(trim => trim?.id === trimOption?.value)
              )
            }
            onFocus={onFocusDropdown}
            onMoreInfoContentClick={() => {
              onEvent?.({
                name: EVENTS.VEHICLE_FITMENT_TRIM_NEED_HELP
              });
            }}
            options={trimOptions}
            placeholder="Trim"
            selectedOption={selectedTrimOption}
          />
          {Array.isArray(rideHeightOptions) && rideHeightOptions.length > 1 && (
            <FitmentSelect
              heading={
                <p>
                  What is your {selectedModelOption?.label}&apos;s{' '}
                  <b>ride height</b>?
                </p>
              }
              onChange={rideHeightOption =>
                onRideHeightSelected(
                  rideHeights?.find(
                    rideHeight => rideHeight?.id === rideHeightOption?.value
                  )
                )
              }
              options={rideHeightOptions}
              placeholder="Ride Height"
              selectedOption={selectedRideHeightOption}
              subject={selectedModelOption?.label}
            />
          )}
        </div>
      )}

      {selectedCrumb === CRUMBS.TIRE_WHEEL_SIZE && (
        <FitmentTireSize
          assemblies={assemblies}
          getOptionalSizes={getOptionalSizes}
          isEditing={isEditing}
          isYearAfterModel={isYearAfterModel}
          onEvent={onEvent}
          onShopProducts={(
            selectedAssembly,
            selectedOptionalSize,
            sizeQualifier
          ) => {
            onShopProducts(
              selectedAssembly,
              selectedOptionalSize,
              sizeQualifier
            );
            if (isTreadwellStart) {
              onCrumbSelected(CRUMBS.DRIVING_LOCATION);
            } else {
              onCrumbSelected('');
            }
          }}
          renderShopProductsButton={renderShopProductsButton}
          selectedAssembly={selectedAssembly}
          selectedCrumb={selectedCrumb}
          selectedFitment={temporaryFitment || selectedFitment}
          selectedModel={selectedModel}
          selectedRideHeight={selectedRideHeight}
          selectedTrim={selectedTrim}
          selectedYear={selectedYear}
          setSelectedAssembly={setSelectedAssembly}
          shouldCallShopProducts={shouldCallShopProducts}
          showShopProductsButton={showShopProductsButton}
          textOverrides={textOverrides}
        />
      )}

      {selectedCrumb === CRUMBS.DRIVING_LOCATION && (
        <div styleName="driving-location">
          {renderZipCodeInput?.({
            onSubmit: () => {
              onCrumbSelected(CRUMBS.DRIVING_TYPE);
            }
          })}
        </div>
      )}

      {selectedCrumb === CRUMBS.DRIVING_TYPE && (
        <>
          <hr styleName="hr" />
          {renderDriverTypesInput?.()}
        </>
      )}

      {selectedCrumb === '' &&
        Boolean(selectedFitment) &&
        typeof renderSelectedFitment === 'function' &&
        renderSelectedFitment()}
    </>
  );
}

FitmentByVehicle.propTypes = {
  assemblies: PropTypes.arrayOf(assemblyPropType),
  canToggleVehicle: PropTypes.bool,
  defaultVehicleLicensePlateAddress: PropTypes.object,
  getOptionalSizes: PropTypes.func,
  hideBackButton: PropTypes.bool,
  hideDelete: PropTypes.bool,
  hideLicensePlate: PropTypes.bool,
  hideVehicleCrumbs: PropTypes.bool,
  isCustomDriverTypeShowing: PropTypes.bool,
  isEditing: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isShowingRecentFitments: PropTypes.bool.isRequired,
  isTreadwellStart: PropTypes.bool,
  isYearAfterModel: PropTypes.bool,
  makes: PropTypes.arrayOf(PropTypes.string),
  models: PropTypes.arrayOf(modelPropType),
  onBackToMyVehicles: PropTypes.func,
  onCrumbSelected: PropTypes.func,
  onEvent: PropTypes.func,
  onFitmentSelected: PropTypes.func,
  onFitmentsToggled: PropTypes.func,
  onFocusDropdown: PropTypes.func,
  onFocusLicensePlateDropdown: PropTypes.func,
  onLicensePlateCancel: PropTypes.func,
  onLicensePlateVehicleConfirmed: PropTypes.func,
  onMakeSelected: PropTypes.func,
  onModelSelected: PropTypes.func,
  onRecentFitmentRemoved: PropTypes.func,
  onRideHeightSelected: PropTypes.func,
  onSavedFitmentRemoved: PropTypes.func,
  onSelectFitment: PropTypes.func,
  onShopProducts: PropTypes.func,
  onSubmitLicensePlate: PropTypes.func,
  onTrimSelected: PropTypes.func,
  onYearSelected: PropTypes.func,
  recentFitments: PropTypes.arrayOf(PROP_TYPES.fitment),
  renderDriverTypesInput: PropTypes.func,
  renderFitmentBanner: PropTypes.func,
  renderSavedFitmentsMessage: PropTypes.func,
  renderSelectedFitment: PropTypes.func,
  renderShopProductsButton: PropTypes.func,
  renderZipCodeInput: PropTypes.func,
  rideHeights: PropTypes.arrayOf(rideHeightPropType),
  savedFitments: PropTypes.arrayOf(PROP_TYPES.fitment),
  selectedAssembly: assemblyPropType,
  selectedCrumb: PropTypes.string,
  selectedDriverType: PropTypes.string,
  selectedFitment: PROP_TYPES.fitment,
  selectedMake: PropTypes.string,
  selectedModel: modelPropType,
  selectedRideHeight: rideHeightPropType,
  selectedTrim: trimPropType,
  selectedYear: PropTypes.any,
  setIsShowingRecentFitments: PropTypes.func,
  setSelectedAssembly: PropTypes.func,
  shouldAutofocusLicensePlate: PropTypes.bool,
  shouldCallShopProducts: PropTypes.bool,
  showShopProductsButton: PropTypes.bool,
  temporaryFitment: PROP_TYPES.fitment,
  textOverrides: PropTypes.object,
  trims: PropTypes.arrayOf(trimPropType),
  years: PropTypes.any,
  zipCodeValue: PropTypes.string
};

export default FitmentByVehicle;
